import classnames from "classnames"
import { ElementType, ReactElement, cloneElement, forwardRef, isValidElement } from "react"
import { Box, PolymorphicRef, ComponentUsingBox } from "../Box"
import { FOCUS_STATE_BASE_CLASSES } from "../utils/focusStateClasses"
import {
  COLOUR_SCHEMES,
  BASE_CLASSES,
  BASE_CLASSES_MD,
  BASE_CLASSES_SM,
  COLOUR_SCHEMES_HOVER,
  BASE_CLASSES_DISABLED,
  COLOUR_SCHEMES_DISABLED,
  BASE_CLASSES_ACTIVE,
  BASE_CLASSES_FOCUS,
  BASE_CLASSES_ENABLED,
  DEFAULT_ELEMENT,
} from "./constants"
import type { ButtonProps, BaseButtonProps } from "./types"
import { getButtonBoxAttrs } from "./getButtonBoxAttrs"
import { LoadingSpinner } from "../LoadingSpinner"

export const Button: ComponentUsingBox<typeof DEFAULT_ELEMENT, BaseButtonProps> = forwardRef(
  <E extends ElementType = typeof DEFAULT_ELEMENT>(
    {
      as: element,
      children: childrenProp,
      className: classNameProp,
      disabled,
      endAdornment,
      href,
      isFullWidth,
      isLoading = false,
      onClick,
      size = "md",
      variation = "primary",
      loadingSpinnerTitle = "Loading",
      startAdornment,
      ...rest
    }: ButtonProps<E>,
    ref: PolymorphicRef,
  ) => {
    const { isDisabledStyling, ...attrs } = getButtonBoxAttrs({
      ...rest,
      as: element,
      disabled,
      href,
      isLoading,
      onClick,
    })

    const isSM = size === "sm"

    const className = classnames(
      COLOUR_SCHEMES[variation],
      BASE_CLASSES,
      FOCUS_STATE_BASE_CLASSES,
      isSM ? BASE_CLASSES_SM : BASE_CLASSES_MD,
      isFullWidth ? "w-full" : "w-fit",
      isDisabledStyling
        ? [BASE_CLASSES_DISABLED, COLOUR_SCHEMES_DISABLED[variation]]
        : [
            BASE_CLASSES_ENABLED,
            BASE_CLASSES_ACTIVE,
            BASE_CLASSES_FOCUS,
            COLOUR_SCHEMES_HOVER[variation],
          ],
      classNameProp,
    )

    const getCloneElementWithSizeProp = (element: ReactElement) =>
      cloneElement(element, {
        size: size === "sm" ? "xs" : "sm",
      })

    const children = (
      <>
        {isLoading && (
          <div className="col-start-1 col-end-2 row-start-1 row-end-2 flex h-full w-full flex-col items-center justify-center">
            <LoadingSpinner aria-hidden size={size} />
            <span className="sr-only">{loadingSpinnerTitle}</span>
          </div>
        )}
        <div
          className={classnames(
            "col-start-1 col-end-2 row-start-1 row-end-2 flex h-full items-center justify-center gap-2",
            isLoading && "invisible",
          )}
        >
          {isValidElement(startAdornment) && getCloneElementWithSizeProp(startAdornment)}
          {childrenProp}
          {isValidElement(endAdornment) && getCloneElementWithSizeProp(endAdornment)}
        </div>
      </>
    )

    return (
      <Box
        {...attrs}
        className={classnames(className, "inline-grid auto-rows-[100%] content-center")} // auto-rows fixes a Safari bug whereby the children overflow the container. See https://bugs.webkit.org/show_bug.cgi?id=256781
        ref={ref}
      >
        {children}
      </Box>
    )
  },
)

Button.displayName = "Button"
